/**
 * Custom Accordion Web Component
 * Handles accordion functionality with smooth height transitions
 */
const ACCORDION_SCROLL_DELAY = 550;
const ACCORDION_SCROLL_OFFSET = 100;

class CustomAccordion extends HTMLElement {
  connectedCallback() {
    this.initializeAccordions();
    this.handleAnchors();
    this.setupHashChangeListener();
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  initializeAccordions() {
    const accordions = this.querySelectorAll('.accordion-container');

    for (const accordion of accordions) {
      if (!(accordion instanceof HTMLElement)) continue;

      const button = accordion.querySelector('.accordion-button');
      if (!(button instanceof HTMLElement)) continue;

      button.addEventListener('click', () => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        this.updateAccordionState(accordion, !isExpanded);
      });
    }
  }

  handleAnchors() {
    // Check if there's a hash in the URL on page load
    this.openTargetedAccordion();
  }

  setupHashChangeListener() {
    // Listen for hash changes (e.g., when user clicks a link with anchor)
    window.removeEventListener('hashchange', this.handleHashChange);
    window.addEventListener('hashchange', this.handleHashChange);
  }

  /**
   * Opens an accordion based on the URL hash
   * @param {string} hash - The URL hash (including the # symbol)
   */
  openAccordionFromHash(hash) {
    const anchorId = decodeURIComponent(hash.substring(1).trim());
    if (!anchorId) return;

    const targetElement = document.getElementById(anchorId);
    if (!(targetElement instanceof HTMLElement)) return;

    const targetAccordion = targetElement.classList.contains('accordion-container')
      ? targetElement
      : targetElement.closest('.accordion-container');

    if (!(targetAccordion instanceof HTMLElement) || !this.contains(targetAccordion)) return;

    this.closeOtherAccordions(targetAccordion);
    this.updateAccordionState(targetAccordion, true);
    this.scrollAccordionIntoView(targetAccordion);
  }

  handleHashChange = () => {
    if (!window.location.hash) return;

    this.openAccordionFromHash(window.location.hash);
  };

  openTargetedAccordion() {
    if (window.location.hash) {
      this.openAccordionFromHash(window.location.hash);
      return;
    }

    const url = new URL(window.location.href);
    const targetAccordionId = [
      'faq',
      'faq-section',
      'faqSection',
      'accordion',
      'section'
    ]
      .map((parameterName) => url.searchParams.get(parameterName)?.trim())
      .find(Boolean);

    if (!targetAccordionId) return;

    this.openAccordionFromHash(`#${targetAccordionId}`);
  }

  /**
   * @param {HTMLElement} targetAccordion
   */
  closeOtherAccordions(targetAccordion) {
    const accordions = this.querySelectorAll('.accordion-container');

    for (const accordion of accordions) {
      if (!(accordion instanceof HTMLElement) || accordion === targetAccordion) continue;

      this.updateAccordionState(accordion, false);
    }
  }

  /**
   * @param {HTMLElement} accordion
   * @param {boolean} isExpanded
   */
  updateAccordionState(accordion, isExpanded) {
    const button = accordion.querySelector('.accordion-button');
    const content = accordion.querySelector('.accordion-content');
    const icon = accordion.querySelector('.accordion-icon');

    if (!(button instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    button.setAttribute('aria-expanded', String(isExpanded));
    content.style.height = isExpanded ? `${content.scrollHeight}px` : '0px';
    content.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');

    if (isExpanded) {
      content.removeAttribute('inert');
    } else {
      content.setAttribute('inert', '');
    }

    if (icon instanceof HTMLElement) {
      icon.style.transform = isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)';
    }
  }

  /**
   * @param {HTMLElement} accordion
   */
  scrollAccordionIntoView(accordion) {
    window.setTimeout(() => {
      const offsetTop = accordion.getBoundingClientRect().top + window.scrollY - ACCORDION_SCROLL_OFFSET;

      window.scrollTo({
        top: Math.max(offsetTop, 0),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }, ACCORDION_SCROLL_DELAY);
  }
}

if (!customElements.get('custom-accordion')) {
  customElements.define('custom-accordion', CustomAccordion);
}
